import React, { type PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { FlagsProvider } from '@atlaskit/flag';
import { useSetViewMoreModalParams } from '@atlassian/conversation-assistant-chat-prompt-input/useSetViewMoreModalParams';
import { AnalyticsContextWithSourceAndProperties } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { useSideNavModal } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';

import {
	RovoSettingsConfigProvider,
	type RovoSettingsConfig,
	RovoSettingsModalMountedProvider,
	useIsRovoSettingsModalProviderMounted,
	useRovoSettingsConfig,
} from '../../common/utils/rovo-settings-config';

import { messages } from './messages';
import { RovoSettingsModalSideNavModalProvider } from './side-nav-modal-provider';
import type { RovoSettingsModalProviderProps } from './types';

const DEFAULT_ASSISTANCE_SERVICE_BASE_URL = '/gateway/api/assist';

/**
 * Provider tree for the Rovo Settings modal (config, flags, side-nav modal).
 *
 * Idempotent: if another `RovoSettingsModalProvider` is already mounted above,
 * the inner one becomes a pass-through (with a dev-only `console.warn`).
 */
export const RovoSettingsModalProvider = ({
	product,
	cloudId,
	orgId,
	experienceId = 'ai-mate',
	assistanceServiceConfig,
	onSeeWhatRovoKnows,
	surface,
	disableSettingsModal,
	children,
}: PropsWithChildren<RovoSettingsModalProviderProps>): React.JSX.Element => {
	const existingConfig = useRovoSettingsConfig();
	const isAlreadyWrapped = useIsRovoSettingsModalProviderMounted();
	const hasConfigMismatch =
		isAlreadyWrapped &&
		(existingConfig.product !== product ||
			existingConfig.siteId !== cloudId ||
			existingConfig.orgId !== orgId ||
			existingConfig.surface !== surface ||
			existingConfig.experienceId !== experienceId ||
			existingConfig.assistanceServiceConfig?.baseUrl !==
				(assistanceServiceConfig?.baseUrl ?? DEFAULT_ASSISTANCE_SERVICE_BASE_URL));

	useEffect(() => {
		if (isAlreadyWrapped && process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn(
				hasConfigMismatch
					? `[RovoSettingsModalProvider] Nested RovoSettingsModalProvider detected with different config. ` +
							`The inner provider is safely skipped and the outer provider's config is used ` +
							`for modal behaviour and experiment exposure - see ASIMO-3977. ` +
							`Inner behaviour props (including disableSettingsModal) are ignored.`
					: `[RovoSettingsModalProvider] Nested RovoSettingsModalProvider detected. ` +
							`The inner provider is safely skipped - see ASIMO-3977. ` +
							`If this is intentional defensive wrapping, the outer provider's config is used. ` +
							`Inner behaviour props (including disableSettingsModal) are ignored.`,
			);
		}
	}, [hasConfigMismatch, isAlreadyWrapped]);

	const configValue = useMemo<RovoSettingsConfig>(
		() => ({
			product,
			surface,
			siteId: cloudId,
			orgId,
			experienceId,
			assistanceServiceConfig: {
				baseUrl: assistanceServiceConfig?.baseUrl ?? DEFAULT_ASSISTANCE_SERVICE_BASE_URL,
				headers: assistanceServiceConfig?.headers ?? {},
			},
			onSeeWhatRovoKnows,
		}),
		[product, cloudId, orgId, experienceId, assistanceServiceConfig, onSeeWhatRovoKnows, surface],
	);

	if (isAlreadyWrapped) {
		return <>{children}</>;
	}

	return (
		<RovoSettingsModalMountedProvider value={true}>
			<RovoSettingsConfigProvider value={configValue}>
				<FlagsProvider>
					<RovoSettingsModalSideNavModalProvider disabled={disableSettingsModal}>
						<ViewMoreModalParamsSeeder
							product={product}
							cloudId={cloudId}
							experienceId={experienceId}
						/>
						{children}
					</RovoSettingsModalSideNavModalProvider>
				</FlagsProvider>
			</RovoSettingsConfigProvider>
		</RovoSettingsModalMountedProvider>
	);
};

/**
 * Seeds ViewMoreModalParams so the Connected Apps tab gets the
 * assistanceServiceParams it needs.
 */
const ViewMoreModalParamsSeeder = ({
	product,
	cloudId,
	experienceId,
}: {
	product: string;
	cloudId: string;
	experienceId: string;
}): null => {
	const setParams = useSetViewMoreModalParams();

	useEffect(() => {
		setParams({
			chatModeKey: 'rovo-settings-modal',
			assistanceServiceParams: {
				touchpointSource: 'rovo-settings-modal',
				product,
				experienceId,
				siteId: cloudId,
			},
		});
		return () => setParams(null);
	}, [setParams, product, cloudId, experienceId]);

	return null;
};

export const useRovoSettingsModal = (): {
	open: (options?: { defaultTab?: string }) => void;
} => {
	const sideNavModal = useSideNavModal();
	const { formatMessage } = useIntl();
	const { surface } = useRovoSettingsConfig();

	return useMemo(
		() => ({
			open: (options?: { defaultTab?: string }) => {
				if (sideNavModal.disabled) {
					return;
				}
				sideNavModal.open({
					title: formatMessage(messages.modalTitle),
					navAriaLabel: formatMessage(messages.modalTitle),
					defaultSelectedId: options?.defaultTab,
					surface,
				});
			},
		}),
		[sideNavModal, formatMessage, surface],
	);
};

interface RovoSettingsModalLazyProps extends RovoSettingsModalProviderProps {
	pendingOpenOptions: React.RefObject<{ defaultTab?: string } | undefined>;
	openModalRef: React.MutableRefObject<((options?: { defaultTab?: string }) => void) | null>;
}

/**
 * Lazy-loaded chunk. Mounts the provider tree, registers the open
 * function for direct reuse, and opens the modal on first mount.
 *
 * Wraps with AnalyticsContextWithSourceAndProperties because this entrypoint
 * is used by external surfaces that don't already have an analytics provider.
 * Chat surfaces use RovoSettingsModalProvider directly and provide their own.
 */
export const RovoSettingsModalLazy = ({
	pendingOpenOptions,
	openModalRef,
	...providerProps
}: RovoSettingsModalLazyProps): React.JSX.Element => {
	return (
		<AnalyticsContextWithSourceAndProperties source="AIMate">
			<RovoSettingsModalProvider {...providerProps}>
				<ModalOpener pendingOpenOptions={pendingOpenOptions} openModalRef={openModalRef} />
			</RovoSettingsModalProvider>
		</AnalyticsContextWithSourceAndProperties>
	);
};

// Opens the modal on first mount and registers the open function
// so subsequent trigger clicks can call it directly without re-mounting.
const ModalOpener = ({
	pendingOpenOptions,
	openModalRef,
}: {
	pendingOpenOptions: React.RefObject<{ defaultTab?: string } | undefined>;
	openModalRef: React.MutableRefObject<((options?: { defaultTab?: string }) => void) | null>;
}): null => {
	const { open } = useRovoSettingsModal();
	const openRef = useRef(open);
	openRef.current = open;

	// Register so the trigger can call open directly on subsequent clicks
	openModalRef.current = open;

	useEffect(() => {
		openRef.current(pendingOpenOptions.current ?? undefined);
	}, [pendingOpenOptions, openRef]);

	return null;
};
